import React, { type ReactNode } from "react";

// 3rd-party Libraries
import { useFlags } from "launchdarkly-react-client-sdk";

import { MenuItem } from "primereact/menuitem";
import { BreadCrumb } from "primereact/breadcrumb";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";

// Toolkit Imports
import { useUserContext } from "../../contexts/User/UserContextProvider";
import { useNavContext } from "../../contexts/Nav/NavContextProvider";
import { useBreadcrumbContextOptional } from "../../contexts/Breadcrumb/BreadcrumbProvider";
import { useSettingsContext } from "../../contexts/Settings/SettingsContextProvider";
import { INTERNAL_ROUTES } from "../../../constants/routes";
import { FEATURES } from "../../../constants/features";
import { useSmartNavigation } from "../../../helpers/navigation";
import { useOnlineStatus } from "../../../hooks/useOnlineStatus";
import { useHideAssessmentsForBundle } from "../../../hooks/useHideAssessmentsForBundle";

// Sub-Components
import { NavSearchForm } from "./NavSearchBar/NavSearchBar";
import { Notifications } from "./Notifications/Notifications";
import { NavLogo } from "./NavLogo/NavLogo";
import { ProfileMenu } from "./ProfileMenu/ProfileMenu";
import { type MobileNavEntry } from "./MobileNav/MobileNavDrawer";
import { DesktopNavBar } from "./DesktopNavBar";
import { MobileNavBar } from "./MobileNavBar";
import { useMediaQuery } from "../../../hooks/useMediaQuery";

// Styles
import S from "./UserNavBar.module.scss";

const MEDIA_QUERY_NAV_COLLAPSE = "(max-width: 960px)";

export interface UserNavBarProps {
  breadcrumbs?: MenuItem[];
  dialogTemplate?: (props: {
    showDialog: boolean;
    setShowDialog: (value: boolean) => void;
    onConfirm: () => void;
  }) => ReactNode;
  navbarRef?: React.RefObject<HTMLDivElement>;
  /** Prefix used to build the data-testid on the root <nav>: `${testId}-breadcrumb` or `${testId}-menubar` depending on layout mode. */
  testId?: string;
}

export function UserNavBar({
  breadcrumbs,
  dialogTemplate,
  navbarRef,
  testId = "user-nav-bar",
}: UserNavBarProps) {
  const { aspenTenantAdminChat } = useFlags();
  const {
    // Basic state flags
    isLoading,
    isAuthenticated,

    // SJ Admin specific flags
    isSjAdmin,
    impersonatedEmail,

    // User and tenant info
    user,
    selectedTenant,
    isTenantAdmin,
    isRestrictedBundle,

    // UI state
    isTourVisible,

    // State setters and toggles
    setUser,
    toggleTourVisibility,
    sjAdminLeaveTenant,
    sjAdminStopImpersonation,
  } = useUserContext();

  const { settings, isFeatureEnabled } = useSettingsContext();
  const { leaderboard: showLeaderboard, catalog: showCatalog, contacts } = settings || {};

  // Calculate some derived flags
  const showChampionPassport =
    isAuthenticated &&
    !!user?.railsProfile?.hasAccessToDojoActivities &&
    isFeatureEnabled(FEATURES.CHAMPION_PASSPORT);

  const showUserOptions = isAuthenticated && (!isSjAdmin || !!impersonatedEmail);
  const hideAssessments = useHideAssessmentsForBundle();

  // Try to get breadcrumb context (new system) or nav context (legacy)
  const breadcrumbCtx = useBreadcrumbContextOptional();
  const navContext = useNavContext();

  const showConfirmation = !!dialogTemplate;
  const { navigateTo, showDialog, setShowDialog, confirmNavigation } = useSmartNavigation({
    showConfirmation,
  });

  const displayBreadcrumbs = breadcrumbCtx
    ? breadcrumbCtx.display === "navbar"
      ? breadcrumbCtx.breadcrumbs
      : undefined
    : (navContext?.breadcrumbs ?? breadcrumbs);

  // Dynamic home item: uses BreadcrumbProvider's homeItem if available, otherwise defaults to Learn
  const defaultHome = {
    label: INTERNAL_ROUTES.Learn.name,
    command: () => navigateTo(INTERNAL_ROUTES.Learn.route),
  };
  const homeItem = breadcrumbCtx?.homeItem ?? defaultHome;

  // The desktop menu items don't have room to breathe below 900px, so the
  // hamburger/drawer nav is used below that instead.
  const isMobile = useMediaQuery(MEDIA_QUERY_NAV_COLLAPSE);
  const isOnline = useOnlineStatus();

  // Event handlers
  const handleLeaveTenant = async () => {
    await sjAdminLeaveTenant();
  };

  /**
   * Main navigation entries, shared by the desktop Menubar and the mobile
   * drawer, so visibility rules live in exactly one place.
   */
  const learnEntry: MobileNavEntry = showChampionPassport
    ? {
        label: INTERNAL_ROUTES.Learn.name,
        visible: showUserOptions,
        children: [
          { label: "Dashboard", route: INTERNAL_ROUTES.Learn.route },
          {
            label: "Champion Passport",
            route: INTERNAL_ROUTES.Champions.route,
            // champions is a separate, uncached app — hide rather than dead-end offline
            visible: isOnline,
          },
        ],
      }
    : {
        label: INTERNAL_ROUTES.Learn.name,
        visible: showUserOptions,
        route: INTERNAL_ROUTES.Learn.route,
      };

  const navEntries: MobileNavEntry[] = [
    learnEntry,
    {
      label: INTERNAL_ROUTES.Learn.routes.assessments.name,
      visible: showUserOptions && !hideAssessments,
      route: INTERNAL_ROUTES.Learn.routes.assessments.route,
    },
    {
      label: INTERNAL_ROUTES.Learn.routes.catalog.name,
      visible: showUserOptions && showCatalog,
      route: INTERNAL_ROUTES.Learn.routes.catalog.route,
    },
    {
      label: INTERNAL_ROUTES.Learn.routes.leaderboard.name,
      visible: showLeaderboard,
      route: INTERNAL_ROUTES.Learn.routes.leaderboard.route,
    },
    {
      label: INTERNAL_ROUTES.Admin.name,
      visible: isTenantAdmin,
      route: INTERNAL_ROUTES.Admin.route,
    },
    {
      label: INTERNAL_ROUTES.Learn.routes.aspenChat.name,
      visible: isTenantAdmin && !!aspenTenantAdminChat && !isRestrictedBundle,
      route: INTERNAL_ROUTES.Learn.routes.aspenChat.route,
      badge: "Beta",
    },
  ];

  // Calculate how many visible main nav items there are, shared by both DesktopNavBar
  // (Menubar item count) and MobileNavBar (drawer entry count).
  const visibleItemCount = navEntries.reduce(
    (count, entry) => (entry.visible === false ? count : count + 1),
    0
  );

  const end = (
    <>
      {isSjAdmin && (
        <div className="flex items-center gap-2">
          <Badge value={`SJ Admin (${selectedTenant})`} severity="info" />
          {!impersonatedEmail && <Button label="Leave Tenant" onClick={handleLeaveTenant} />}
        </div>
      )}
      {showUserOptions && (
        <>
          <NavSearchForm menuItemCount={visibleItemCount} />
          <Notifications />
          <ProfileMenu
            user={user}
            isAuthenticated={isAuthenticated}
            isLoading={isLoading}
            isTourVisible={isTourVisible}
            toggleTourVisibility={toggleTourVisibility}
            navigateTo={navigateTo}
            setUser={setUser}
            selectedTenant={selectedTenant}
            isSjAdmin={isSjAdmin}
            impersonatedEmail={impersonatedEmail}
            sjAdminLeaveTenant={sjAdminLeaveTenant}
            sjAdminStopImpersonation={sjAdminStopImpersonation}
            contacts={contacts}
          />
        </>
      )}
    </>
  );

  if (displayBreadcrumbs) {
    return (
      <>
        <nav
          ref={navbarRef}
          className={`${S.menubar} ${S.breadcrumbContainer}`}
          aria-label="Main navigation"
          data-testid={`${testId}-breadcrumb`}
        >
          <div className={S.breadcrumbStart}>
            <NavLogo
              navigateTo={navigateTo}
              thumbLogoUrl={settings?.thumbLogoUrl}
              addMargin={true}
            />
            <BreadCrumb
              model={displayBreadcrumbs}
              home={homeItem}
              pt={{
                root: { "aria-label": "Breadcrumb" },
                action: { className: "body-sm" },
                separator: { role: "listitem" },
              }}
            />
          </div>
          <div className={S.breadcrumbEnd}>
            <Notifications />
            <ProfileMenu
              user={user}
              isAuthenticated={isAuthenticated}
              isLoading={isLoading}
              isTourVisible={isTourVisible}
              toggleTourVisibility={toggleTourVisibility}
              navigateTo={navigateTo}
              setUser={setUser}
              selectedTenant={selectedTenant}
              isSjAdmin={isSjAdmin}
              impersonatedEmail={impersonatedEmail}
              sjAdminLeaveTenant={sjAdminLeaveTenant}
              sjAdminStopImpersonation={sjAdminStopImpersonation}
              contacts={contacts}
            />
          </div>
        </nav>
        {dialogTemplate?.({
          showDialog,
          setShowDialog,
          onConfirm: confirmNavigation,
        })}
      </>
    );
  }

  // Only one of these is ever mounted, so component-local state (e.g. MobileNavBar's
  // drawer-open flag) resets for free whenever the breakpoint is crossed.
  return isMobile ? (
    <MobileNavBar
      navbarRef={navbarRef}
      testId={testId}
      thumbLogoUrl={settings?.thumbLogoUrl}
      navigateTo={navigateTo}
      navEntries={navEntries}
      end={end}
      dialogTemplate={dialogTemplate}
      showDialog={showDialog}
      setShowDialog={setShowDialog}
      confirmNavigation={confirmNavigation}
    />
  ) : (
    <DesktopNavBar
      navbarRef={navbarRef}
      testId={testId}
      thumbLogoUrl={settings?.thumbLogoUrl}
      navigateTo={navigateTo}
      navEntries={navEntries}
      end={end}
      dialogTemplate={dialogTemplate}
      showDialog={showDialog}
      setShowDialog={setShowDialog}
      confirmNavigation={confirmNavigation}
    />
  );
}
